/**
 * «Плитка рядом с экраном»: для того, что браузер сам лениво грузить не умеет. Главный случай — обложка ролика
 * (атрибут poster): у <video> нет loading="lazy", и poster браузер качает сразу, у всех плиток страницы.
 * Замер 30.09 (первый заход на /bots/trends, медленный 4G): готовая страница тянула 31 обложку, 746 КБ, почти
 * все за экраном, — они делили канал со стилями и скриптом, и экран стоял чёрным 7,7 с; без них — 5,0 с.
 *
 * Запас 800px по вертикали: при обычной прокрутке обложка успевает приехать раньше, чем плитка покажется.
 * Плитки горизонтальной полки за её краем обрезаны самой полкой: scrollMargin (Chrome/Android 120+) расширяет и
 * её — обложки приезжают на плитку вперёд; где его нет (Safari), плитка станет «рядом», когда полку пролистают
 * (так же ведут себя картинки с loading="lazy").
 */
import { useEffect, useState, type RefObject } from 'react'

const ROOT_MARGIN = '800px 0px'
const SCROLL_MARGIN = '0px 320px'

const callbacks = new Map<Element, () => void>()
let observer: IntersectionObserver | null = null

/** Позвать onNear один раз, когда элемент окажется рядом с экраном. Возвращает отписку. */
export function watchNear(el: Element, onNear: () => void): () => void {
	if (typeof IntersectionObserver === 'undefined') {
		onNear()
		return () => {}
	}
	if (!observer) {
		observer = new IntersectionObserver(
			(entries) => {
				for (const e of entries) {
					if (!e.isIntersecting) continue
					const cb = callbacks.get(e.target)
					callbacks.delete(e.target)
					observer?.unobserve(e.target)
					cb?.()
				}
			},
			// scrollMargin знают не все браузеры и не все версии типов DOM — неизвестный ключ браузер пропускает.
			{ rootMargin: ROOT_MARGIN, scrollMargin: SCROLL_MARGIN } as IntersectionObserverInit,
		)
	}
	callbacks.set(el, onNear)
	observer.observe(el)
	return () => {
		callbacks.delete(el)
		observer?.unobserve(el)
	}
}

/** true — элемент хоть раз был рядом с экраном (и останется true). enabled=false — не следим, сразу false. */
export function useNearViewport(ref: RefObject<Element | null>, enabled = true): boolean {
	const [near, setNear] = useState(false)
	useEffect(() => {
		const el = ref.current
		if (near || !enabled || !el) return
		return watchNear(el, () => setNear(true))
	}, [near, enabled, ref])
	return near
}
